import { useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import PageHeader from "@/components/common/PageHeader";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import { describeChange, MetricPanel, type Metric } from "@/components/dashboard/metric-cell";
import { PeriodToggle } from "@/components/dashboard/period-toggle";
import { describeWaiting, submissionActivity } from "@/components/content-programs/submission-activity";
import { listAffiliateEligiblePrograms, listAffiliateMySubmissions, isRunningAd } from "@/services/contentProgram.service";
import { formatNumber } from "@/utils/formatters";

type Tab = "programs" | "submissions";

const PATHS: Record<Tab, string> = {
  programs: "/affiliate/content-programs",
  submissions: "/affiliate/content-programs/submissions",
};

/**
 * The affiliate's content programs: one header, two views of the same
 * work (programs to submit to, what has been submitted) and an activity
 * strip that both share.
 */
export default function AffiliateContentProgramsLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const tab: Tab = pathname.startsWith(PATHS.submissions) ? "submissions" : "programs";

  const programsQuery = useQuery({
    queryKey: ["affiliate-eligible-content-programs"],
    queryFn: listAffiliateEligiblePrograms,
  });
  const submissionsQuery = useQuery({
    queryKey: ["affiliate-my-content-submissions"],
    queryFn: listAffiliateMySubmissions,
  });

  const rows = useMemo(() => submissionsQuery.data ?? [], [submissionsQuery.data]);

  const metrics = useMemo<Metric[]>(() => {
    const activity = submissionActivity(rows, days);
    const waiting = rows.filter((r) => r.status === "PENDING");
    const oldest = waiting.reduce<string | null>((acc, r) => (acc === null || r.createdAt < acc ? r.createdAt : acc), null);
    const changes = rows.filter((r) => r.status === "CHANGE_REQUESTED").length;
    const approved = rows.filter((r) => r.status === "APPROVED" || r.status === "IN_USE").length;
    const running = rows.filter(isRunningAd).length;
    const rejected = rows.filter((r) => r.status === "REJECTED").length;
    const reviewed = approved + rejected;
    return [
      {
        id: "acp-submissions",
        label: "Submitted",
        value: formatNumber(activity.current),
        change: describeChange(activity.current, activity.previous, days, formatNumber),
        series: activity.series,
        unit: "submission",
      },
      {
        id: "acp-waiting",
        label: "Awaiting review",
        value: formatNumber(waiting.length),
        change: describeWaiting(oldest, changes > 0 ? `${formatNumber(changes)} need changes` : "Nothing waiting"),
      },
      {
        id: "acp-approved",
        label: "Approved",
        value: formatNumber(approved),
        change: reviewed === 0 ? "No reviews yet" : `${Math.round((approved / reviewed) * 100)}% of reviewed`,
      },
      {
        id: "acp-running",
        label: "Running as ads",
        value: formatNumber(running),
        change: running === 0 ? "Approved creative can become an ad" : "You earn commission on their sales",
      },
    ];
  }, [rows, days]);

  return (
    <div className="space-y-4">
      <PageHeader title="Content programs" />

      <UnderlineTabs
        tabs={[
          { value: "programs" as const, label: "Programs", count: programsQuery.data?.length ?? null },
          { value: "submissions" as const, label: "Submissions", count: submissionsQuery.data?.length ?? null },
        ]}
        value={tab}
        onValueChange={(next) => navigate(PATHS[next])}
        accent="brand"
      />

      <MetricPanel
        id="acp-activity"
        title="Activity"
        metrics={metrics}
        columns="lg:grid-cols-4"
        loading={submissionsQuery.isLoading}
        action={<PeriodToggle value={days} onChange={setDays} disabled={submissionsQuery.isLoading} />}
      />

      <Outlet />
    </div>
  );
}
